<template>
    <tr
        :class="[
            record[primaryKey] in rowWarningOptions ? 'no-stock' : '',
            getRowClass(record, index),
            { 'selected-row': selected },
            { 'compact-design': localCompactView },
            { 'merged-row': isMergedRow(record) },
        ]"
        :id="'row_' + record[primaryKey]"
        v-show="isRecordVisible(record)"
        @click="
            selectSingle
                ? selectSingleHandler($event, record[primaryKey])
                : selectMultiple
                ? selectMultipleHandler($event, record[primaryKey])
                : null;
            emitOnClick ? datatableCtx.$emit('emit_on_click', record) : null;
        "
        @click.right="showContextMenu($event, record, tableId)"
        @mouseenter="addHoverClass"
        @mouseleave="removeHoverClass"
    >
        <td v-if="hasIndexColumn">{{ index + 1 }}</td>
        <td
            v-if="hasRowControlColumn()"
            class="row-control-cell"
            :style="getRowControlColumnStyle()"
        >
            <slot
                name="row_control_column"
                :record="record"
            ></slot>
        </td>
        <!-- able to select row -->
        <template v-if="hasVisibleColumns">
            <td
                v-if="dragHandleVisible"
                class="row-checkbox-cell"
                :style="getSelectColumnStyle()"
            >
                <i
                    class="icon-dragindicator"
                    style="cursor: pointer"
                ></i>
            </td>
            <td
                v-if="selectSingle && selectableRecords[record[primaryKey]] != null"
                class="row-checkbox-cell"
                :style="getSelectColumnStyle()"
            >
                <input
                    type="radio"
                    :id="formattedDatatableUniqueKey + '_select_row_' + record[primaryKey]"
                    :checked="record.checked"
                    :disabled="disableSelectedRow(record)"
                    @click.stop="selectSingleHandler($event, record[primaryKey])"
                />
            </td>
            <td
                v-else-if="selectMultiple && selectableRecords[record[primaryKey]] != null"
                class="row-checkbox-cell"
                :style="getSelectColumnStyle()"
            >
                <input
                    class="select_row"
                    type="checkbox"
                    :id="formattedDatatableUniqueKey + '_select_row_' + record[primaryKey]"
                    :value="record.checked"
                    :checked="record.checked"
                    :disabled="disableSelectedRow(record)"
                    @change="selectAllRecordsValidation(record.checked)"
                />
            </td>
            <td
                v-else-if="selectSingle || selectMultiple"
                class="row-checkbox-cell"
                :style="getSelectColumnStyle()"
            >
                <input
                    :id="formattedDatatableUniqueKey + '_select_row_' + record[primaryKey]"
                    :type="selectSingle ? 'radio' : 'checkbox'"
                    :disabled="true"
                />
            </td>
        </template>
        <template v-for="(column, columnKey) in columns">
            <td
                :id="column.id + '_body_' + column.type"
                :class="[
                    column.id.replace('_', '-'),
                    column.type + '-cell',
                    {
                        'is-collapsible':
                            0 === columnKey && hasSubrecord && record.subrecords && record.subrecords.length,
                        'error-td': checkHasError(record, column.id),
                        'mapping-error-td': record.mapping_error ?? false,
                        'editable-cell': isRecordEditable(record, column),
                    },
                    column.additional_classes,
                    conditionalCustomClasses(record, column.id), // load custom class by condition
                ]"
                :key="column.id"
                v-if="!column.disabled && shouldRenderBodyColumn(record, column)"
                :colspan="getBodyColumnColspan(record, column)"
                v-show="column.visible"
                @mouseenter="handleErrorMessageMouseEnter($event, tableId, column.id, record)"
                @mouseleave="handleErrorMessageMouseLeave"
            >
                <!-- if have subrecord, show arrow at first column -->
                <i
                    v-if="0 === columnKey && typeof record.subrecords !== 'undefined' && record.subrecords.length"
                    :class="[
                        'icon-chevronright',
                        'cursor-pointer',
                        'toggle',
                        {
                            collapsed: record.subrecordVisibility !== true,
                            expanded: record.subrecordVisibility === true,
                        },
                    ]"
                    @click="changeSubRecordVisibility(record)"
                ></i>

                <div
                    class="d-flex align-items-center"
                    :class="{
                        'error-td-message': getErrorMessage(record, column.id),
                        'justify-content-center w-100': column.type == 'checkbox' && column.id?.startsWith('_'),
                    }"
                    :style="
                        column.type == 'checkbox' && column.id?.startsWith('_')
                            ? { paddingRight: '24px', paddingLeft: '8px' }
                            : {}
                    "
                >
                    <!-- {{ checkHasError(record, column.id) ? "error" : "" }} -->
                    <render-slot
                        v-if="hasCtxSlot(`${column.id}_column`)"
                        :fn="ctxSlot(`${column.id}_column`)"
                        :params="{
                            column: column,
                            record: record,
                            editRecordHandler: editRecordFn(record[primaryKey], column),
                            getData: getData,
                        }"
                    ></render-slot>
                    <template v-else>
                        <!-- if data can edit -->
                        <template
                            v-if="true == editable && true == column.editable && isRecordEditable(record, column)"
                        >
                            <bo-cell-number
                                v-if="'number' == column.type"
                                class="w-100"
                                :id="column.id + '_body_' + column.type"
                                :decimal="column.decimal"
                                :currency="column.currency"
                                :negative="column.negative"
                                :sign="column.sign"
                                :min="column.min"
                                :max="column.maxTransform ? column.maxTransform(record) : null"
                                :displayMax="column.displayMax"
                                :setToMinValue="column.setToMinValue"
                                :placeholder="column.placeholder"
                                :editable="true"
                                :readonly="false"
                                :input-style="['text-right', getClasses(record, column.id)]"
                                :value="
                                    typeof column.transform != 'undefined'
                                        ? column.transform(record)
                                        : record[column.id]
                                "
                                :eager="column.eager"
                                @change="editRecordHandler(record[primaryKey], $event, column)"
                            >
                            </bo-cell-number>

                            <bo-cell-currency
                                v-else-if="'currency' == column.type"
                                :id="column.id + '_body_' + column.type"
                                :decimal="typeof column.decimal === 'function' ? column.decimal(record) : column.decimal"
                                :currency="typeof column.currency === 'function' ? column.currency(record) : column.currency"
                                :negative="column.negative"
                                :sign="column.sign"
                                :min="column.min"
                                :max="column.max"
                                :editable="true"
                                :value="
                                    typeof column.transform != 'undefined'
                                        ? column.transform(record)
                                        : record[column.id]
                                "
                                :eager="column.eager"
                                @change="editRecordHandler(record[primaryKey], $event, column)"
                            >
                            </bo-cell-currency>

                            <bo-date-range
                                v-else-if="'date_range' == column.type"
                                :id="column.id + '_body_' + column.type"
                                :editable="true"
                                :value="
                                    typeof column.transform != 'undefined'
                                        ? column.transform(record)
                                        : record[column.id]
                                "
                                :start="record[column.start_date_id]"
                                :end="record[column.end_date_id]"
                                :separator="column.separator"
                                :defaultStartText="column.defaultStartText"
                                :defaultEndText="column.defaultEndText"
                                :defaultEmptyText="column.defaultEmptyText"
                                :showSeparatorIfEmpty="column.showSeparatorIfEmpty"
                                :showNullIfEmpty="column.showNullIfEmpty"
                                :includeEndDay="column.includeEndDay"
                                :simple="true"
                            >
                            </bo-date-range>

                            <bo-cell-attachment
                                v-else-if="'attachment' == column.type"
                                :value="
                                    typeof column.transform != 'undefined'
                                        ? column.transform(record)
                                        : record[column.id]
                                "
                                :id="column.id + '_body_' + column.type"
                                :meta="column.meta"
                                :editable="true"
                                :url="column.url.uploadUrl"
                                @change="editRecordHandler(record[primaryKey], $event, column)"
                                @loading="showLoading"
                                @done_loading="hideLoading"
                            >
                            </bo-cell-attachment>

                            <bo-cell-status
                                v-else-if="'status' == column.type"
                                :value="
                                    typeof column.transform != 'undefined'
                                        ? column.transform(record)
                                        : record[column.id]
                                "
                                :id="column.id"
                                :editable="true"
                                :editableCondition="column.editableCondition"
                                :options="column.options"
                                @change="editRecordHandler(record[primaryKey], $event, column)"
                            >
                            </bo-cell-status>

                            <bo-select
                                v-else-if="'select' == column.type"
                                :value="
                                    typeof column.transform != 'undefined'
                                        ? column.transform(record)
                                        : record[column.id]
                                "
                                :disabled="!column.editable"
                                :readonly="!column.editable"
                                :id="column.id + '_body_' + column.type"
                                :options="column.options"
                                :labels="column.labels"
                                :multiple="column.multiple"
                                :nullable="column.nullable"
                                :nullText="column.nullText"
                                :plain="column.plain"
                                :suffix="column.suffix_id ? record[column.suffix_id] : null"
                                @change="editRecordHandler(record[primaryKey], $event, column)"
                            >
                            </bo-select>

                            <bo-cell-product
                                v-else-if="'product' == column.type"
                                :ref="'assignProduct_' + record[primaryKey]"
                                :id="column.id"
                                :parentId="id"
                                :value="
                                    typeof column.transform != 'undefined'
                                        ? column.transform(record)
                                        : record[column.id]
                                "
                                :editable="true"
                                :getProductUrl="column.url.advanceSearchProductUrl"
                                :getProductOptionsUrl="column.url.advanceSearchProductOptionsUrl"
                                :columns="column.meta.advanceSearchColumns"
                                @change="editRecordHandler(record[primaryKey], $event, column)"
                            ></bo-cell-product>

                            <bo-cell-multiselect
                                v-else-if="'multiselect' == column.type"
                                :ref="'assignMultiselect_' + record[primaryKey]"
                                :id="column.id"
                                :title="resolvePlaceholders(column.title, record)"
                                :subtitle="column.subtitle"
                                :parentId="id"
                                :value="
                                    typeof column.transform != 'undefined'
                                        ? column.transform(record)
                                        : record[column.id]
                                "
                                :meta="column.meta"
                                :editable="true"
                                :selectSingleItem="column.selectSingleItem"
                                :getItemsUrl="resolveURL(column.url.getItemsUrl, record[primaryKey])"
                                :columns="column.meta.addItemsColumns"
                                @change="editRecordHandler(record[primaryKey], $event, column)"
                                @add="$emit(`multiselect_add_${column.id} `, record[primaryKey], $event)"
                                @delete="$emit(`multiselect_delete_${column.id} `, record[primaryKey], $event)"
                            ></bo-cell-multiselect>

                            <bo-cell-per-total
                                v-else-if="'per_total' == column.type"
                                :ref="'perTotal_' + record[primaryKey]"
                                :id="column.id"
                                :parentId="id"
                                :meta="column.meta"
                                :editable="true"
                                :eager="column.eager"
                                :value="
                                    typeof column.transform != 'undefined'
                                        ? column.transform(record)
                                        : record[column.id]
                                "
                                @change="editRecordHandler(record[primaryKey], $event, column)"
                            >
                            </bo-cell-per-total>

                            <bo-cell-mode-input-number
                                v-else-if="'mode_input_number' == column.type"
                                :id="column.id"
                                class="form-control"
                                :value="record[column.id].value"
                                :options="record[column.id].options"
                                :selected="record[column.id].selected"
                                :meta="column.meta"
                                :editable="true"
                                @change="editRecordHandler(record[primaryKey], $event, column)"
                                @value_change="
                                    modifyRecord(
                                        `mode_input_number_value_change_${column.id} `,
                                        record[primaryKey],
                                        $event
                                    )
                                "
                                @selected_change="
                                    modifyRecord(
                                        `mode_input_number_selected_change_${column.id} `,
                                        record[primaryKey],
                                        $event
                                    )
                                "
                            >
                            </bo-cell-mode-input-number>

                            <bo-cell-toggle
                                v-else-if="'toggle' == column.type"
                                :id="column.id + '_body_' + column.type"
                                :value="
                                    typeof column.transform != 'undefined'
                                        ? column.transform(record)
                                        : record[column.id]
                                "
                                :editable="column.editable"
                                @change="editRecordHandler(record[primaryKey], $event, column)"
                            >
                            </bo-cell-toggle>

                            <bo-cell-html
                                v-else-if="'html' == column.type"
                                :value="
                                    typeof column.transform != 'undefined'
                                        ? column.transform(record)
                                        : record[column.id]
                                "
                            ></bo-cell-html>

                            <bo-cell-custom-toggle
                                v-else-if="'custom-toggle' == column.type"
                                :value="
                                    typeof column.transform != 'undefined'
                                        ? column.transform(record)
                                        : record[column.id]
                                "
                                :column="column"
                                :record="record"
                                @change="editRecordHandler(record[primaryKey], $event, column)"
                            >
                            </bo-cell-custom-toggle>

                            <div
                                v-else-if="
                                    'checkbox' == column.type &&
                                    record[column.id] !== undefined &&
                                    record[column.id] !== null
                                "
                                :class="[
                                    'row-checkbox-column',
                                    {
                                        'd-flex justify-content-center w-100 checkbox-header':
                                            column.id?.startsWith('_'),
                                    },
                                ]"
                            >
                                <input
                                    type="checkbox"
                                    v-model="record[column.id]"
                                />
                            </div>

                            <!-- checkbox not applicable for this record: hide the column instead of showing a disabled checkbox -->
                            <div v-else-if="'checkbox' == column.type"></div>

                            <bo-select-popup
                                v-else-if="'select_popup' == column.type"
                                :id="column.id"
                                class="w-100"
                                append-to-body
                                :select-multiple="column?.select_multiple ?? false"
                                :select-single="column?.select_single ?? true"
                                :stop-propagation="column?.stop_propagation ?? true"
                                :editable="editable"
                                :columns="column.columns"
                                :url="column.addRecordsUrl"
                                :add-new-record-url="column.addNewRecordUrl"
                                :initial-records="record[column.id]"
                                :computed-record="column?.computedRecord ?? false"
                                :local-search-sort="column?.localSearchSort ?? false"
                                :clear-selection-after-search="column?.clearSelectionAfterSearch ?? false"
                                :clear-selection-after-sort="column?.clearSelectionAfterSort ?? false"
                                :modal-title="
                                    generalTranslations.select_object.replace(':object', column.label) ??
                                    'Select ' + column.label
                                "
                                :display-id="column.display_id ?? 'full_name'"
                                @trigger="saveRecordstoColumn(record[primaryKey], column, ...arguments)"
                                @change="editRecordHandler(record[primaryKey], $event.target.value, column)"
                            >
                            </bo-select-popup>

                            <render-slot
                                v-else-if="hasCtxSlot(`${column.id}_column`)"
                                :fn="ctxSlot(`${column.id}_column`)"
                                :params="{ record: record, column: column, editRecordHandler: editRecordHandler }"
                            ></render-slot>
                            <template v-else>
                                <input
                                    type="text"
                                    class="form-control"
                                    :value="
                                        typeof column.transform != 'undefined'
                                            ? column.transform(record)
                                            : record[column.id]
                                    "
                                    @change="editRecordHandler(record[primaryKey], $event.target.value, column)"
                                />
                            </template>
                        </template>

                        <!-- if data cannot edit -->
                        <template v-else>
                            <template v-if="true == column.show_tag">
                                <template v-if="typeof record[column.id] == 'object'">
                                    <span
                                        v-for="(subrecord, index) in record[column.id]"
                                        v-if="index < 2"
                                        :key="column.id + '_' + index"
                                        :id="column.id + '_body_' + column.type + '_' + index"
                                        :class="{ 'badge light-gray dark-text': column.show_tag }"
                                    >
                                        {{ subrecord }}
                                    </span>
                                    <span v-if="record[column.id].length < 1">-</span>

                                    <div
                                        v-if="record[column.id].length > 2"
                                        class="dropDownToStatus"
                                    >
                                        <span
                                            id="dropDownToStatus"
                                            data-toggle="dropdown"
                                            aria-haspopup="true"
                                            aria-expanded="false"
                                            class="table-dropdown-circle"
                                        >
                                            +{{ Object.keys(record[column.id]).length - 2 }}
                                        </span>
                                        <div
                                            class="dropdown-menu table-dropdown-menu"
                                            aria-labelledby="dropDownToStatus"
                                        >
                                            <span
                                                v-for="(subrecord, index) in record[column.id]"
                                                v-if="index >= 2"
                                                :class="{
                                                    'badge light-gray dark-text': column.show_tag,
                                                }"
                                                :key="column.id + '_' + index"
                                            >
                                                {{ subrecord }}
                                            </span>
                                        </div>
                                    </div>
                                </template>
                                <span
                                    v-else
                                    :id="column.id + '_body_' + column.type"
                                    :class="{ 'badge light-gray dark-text': column.show_tag }"
                                >
                                    {{ record[column.id] }}
                                </span>
                            </template>
                            <template v-else-if="true == column.list_box">
                                <!-- an empty label map arrives from PHP as [] -->
                                <bo-list-box
                                    :items="Array.isArray(record[column.id]) ? {} : record[column.id]"
                                    :fit-width="column.list_box_fit_width ?? null"
                                ></bo-list-box>
                            </template>
                            <bo-cell-status
                                v-else-if="'status' == column.type"
                                :id="column.id + '_sdfbody_' + column.type"
                                :value="
                                    typeof column.transform != 'undefined'
                                        ? column.transform(record)
                                        : record[column.id]
                                "
                            >
                            </bo-cell-status>

                            <bo-select
                                v-else-if="'select' == column.type"
                                :value="
                                    typeof column.transform != 'undefined'
                                        ? column.transform(record)
                                        : record[column.id]
                                "
                                :disabled="!column.editable"
                                :readonly="!column.editable"
                                :id="column.id + '_body_' + column.type"
                                :options="column.options"
                                :labels="column.labels"
                                :multiple="column.multiple"
                                :nullable="column.nullable"
                                :plain="column.plain"
                                :suffix="column.suffix_id ? record[column.suffix_id] : null"
                                @change="editRecordHandler(record[primaryKey], $event, column)"
                            >
                            </bo-select>

                            <bo-cell-datetime
                                v-else-if="'datetime' == column.type"
                                :id="column.id + '_body_' + column.type"
                                :format="column.format"
                                :value="
                                    typeof column.transform != 'undefined'
                                        ? column.transform(record)
                                        : record[column.id]
                                "
                                :editable="false"
                                :placeholder="column.placeholder"
                                :with-time="column.withTime"
                                :is-end-date="Boolean(column.is_end_date)"
                            >
                            </bo-cell-datetime>

                            <bo-date-range
                                v-else-if="'date_range' == column.type"
                                :id="column.id + '_body_' + column.type"
                                :value="
                                    typeof column.transform != 'undefined'
                                        ? column.transform(record)
                                        : record[column.id]
                                "
                                :start="record[column.start_date_id]"
                                :end="record[column.end_date_id]"
                                :separator="column.separator"
                                :defaultStartText="column.defaultStartText"
                                :defaultEndText="column.defaultEndText"
                                :defaultEmptyText="column.defaultEmptyText"
                                :showSeparatorIfEmpty="column.showSeparatorIfEmpty"
                                :showNullIfEmpty="column.showNullIfEmpty"
                                :includeEndDay="column.includeEndDay"
                                :simple="true"
                            >
                            </bo-date-range>

                            <bo-cell-number
                                v-else-if="'number' == column.type"
                                class="w-100"
                                :id="column.id + '_body_' + column.type"
                                :decimal="column.decimal"
                                :currency="column.currency"
                                :negative="column.negative"
                                :sign="column.sign"
                                :min="column.min"
                                :max="column.maxTransform ? column.maxTransform(record) : null"
                                :displayMax="column.displayMax"
                                :setToMinValue="column.setToMinValue"
                                :placeholder="column.placeholder"
                                :editable="false"
                                :readonly="true"
                                :value="
                                    typeof column.transform != 'undefined'
                                        ? column.transform(record)
                                        : record[column.id]
                                "
                                :eager="column.eager"
                                :input-style="getClasses(record, column.id)"
                            >
                            </bo-cell-number>

                            <bo-cell-currency
                                v-else-if="'currency' == column.type"
                                :id="column.id + '_body_' + column.type"
                                :decimal="typeof column.decimal === 'function' ? column.decimal(record) : column.decimal"
                                :currency="typeof column.currency === 'function' ? column.currency(record) : column.currency"
                                :negative="column.negative"
                                :sign="column.sign"
                                :min="column.min"
                                :max="column.max"
                                :editable="false"
                                :value="
                                    typeof column.transform != 'undefined'
                                        ? column.transform(record)
                                        : record[column.id]
                                "
                                :eager="column.eager"
                            >
                            </bo-cell-currency>

                            <bo-cell-product
                                v-else-if="'product' == column.type"
                                editable="false"
                                :value="
                                    typeof column.transform != 'undefined'
                                        ? column.transform(record)
                                        : record[column.id]
                                "
                            >
                            </bo-cell-product>

                            <bo-cell-multiselect
                                v-else-if="'multiselect' == column.type"
                                :ref="'assignDocument_' + record[primaryKey]"
                                :id="column.id"
                                :parentId="id"
                                :value="
                                    typeof column.transform != 'undefined'
                                        ? column.transform(record)
                                        : record[column.id]
                                "
                                :meta="column.meta"
                                :editable="false"
                            ></bo-cell-multiselect>

                            <bo-cell-per-total
                                v-else-if="'per_total' == column.type"
                                :ref="'perTotal_' + record[primaryKey]"
                                :id="column.id"
                                :parentId="id"
                                :meta="column.meta"
                                :editable="false"
                                :value="
                                    typeof column.transform != 'undefined'
                                        ? column.transform(record)
                                        : record[column.id]
                                "
                            >
                            </bo-cell-per-total>

                            <bo-cell-user
                                v-else-if="'user' == column.type"
                                :id="column.id"
                                :value="
                                    typeof column.transform != 'undefined'
                                        ? column.transform(record)
                                        : record[column.id]
                                "
                            >
                            </bo-cell-user>

                            <bo-cell-link
                                v-else-if="'link' == column.type"
                                :id="column.id + '_body_' + column.type"
                                :link="column.getLink(record)"
                                :label="
                                    typeof column.transform != 'undefined'
                                        ? column.transform(record)
                                        : record[column.id]
                                "
                                :target="column.target"
                            >
                            </bo-cell-link>

                            <bo-cell-link
                                v-else-if="'link2' == column.type"
                                :link="getDetailsPageUrl(record, column)"
                                :label="record[column.id]"
                                :target="column.meta?.target ?? null"
                            >
                            </bo-cell-link>

                            <bo-cell-attachment
                                v-else-if="'attachment' == column.type"
                                :value="
                                    typeof column.transform != 'undefined'
                                        ? column.transform(record)
                                        : record[column.id]
                                "
                                :id="column.id + '_body_' + column.type"
                                :meta="column.meta"
                                :editable="false"
                                @loading="showLoading"
                                @done_loading="hideLoading"
                            >
                            </bo-cell-attachment>

                            <bo-cell-mode-input-number
                                v-else-if="'mode_input_number' == column.type"
                                :id="column.id"
                                class="form-control"
                                :value="record[column.id].value"
                                :options="record[column.id].options"
                                :selected="record[column.id].selected"
                                :meta="column.meta"
                                :editable="false"
                            >
                            </bo-cell-mode-input-number>

                            <bo-cell-html
                                v-else-if="'html' == column.type"
                                :value="
                                    typeof column.transform != 'undefined'
                                        ? column.transform(record)
                                        : record[column.id]
                                "
                            >
                            </bo-cell-html>

                            <!-- <div
                                                    v-else-if="'htmll' == column.type"
                                                    v-html="record[column.id]"
                                                ></div> -->

                            <bo-cell-toggle
                                v-else-if="'toggle' == column.type"
                                :id="column.id + '_body_' + column.type"
                                :value="
                                    typeof column.transform != 'undefined'
                                        ? column.transform(record)
                                        : record[column.id]
                                "
                                :editable="column.editable"
                            >
                            </bo-cell-toggle>

                            <span
                                v-else-if="
                                    'checkbox' == column.type &&
                                    record[column.id] !== undefined &&
                                    record[column.id] !== null
                                "
                                :class="[
                                    'align-items-center',
                                    {
                                        'd-flex justify-content-center': column.id?.startsWith('_'),
                                    },
                                ]"
                                :id="column.id + '_body_' + column.type"
                            >
                                <input
                                    type="checkbox"
                                    v-model="record[column.id]"
                                    disabled
                                />
                            </span>

                            <!-- checkbox not applicable for this record: hide the column instead of showing a disabled checkbox -->
                            <span
                                v-else-if="'checkbox' == column.type"
                                :id="column.id + '_body_' + column.type"
                            ></span>

                            <render-slot
                                v-else-if="hasCtxSlot(`${column.id}_column`)"
                                :fn="ctxSlot(`${column.id}_column`)"
                                :params="{ record: record, column: column, editRecordHandler: editRecordHandler }"
                            ></render-slot>
                            <template v-else>
                                <div
                                    class="d-flex align-items-center max-480 position-relative"
                                    @mouseenter="handleColumnMouseEnter($event, record, column, index)"
                                    @mouseleave="handleColumnMouseLeave(record, column, index)"
                                >
                                    <!-- <span>{{ record[column.id] }}</span> -->
                                    <!-- to handle text with html tag -->
                                    <span
                                        v-if="column?.v_html === false"
                                        :class="record[column.id] === '-' ? 'null' : null"
                                        v-show="column.hide ? (record[column.id] < 0 ? false : true) : true"
                                    >
                                        {{ formatCellContent(record, column) }}
                                    </span>
                                    <span
                                        v-else
                                        :class="record[column.id] === '-' ? 'null' : null"
                                        v-show="column.hide ? (record[column.id] < 0 ? false : true) : true"
                                        v-html="formatCellContent(record, column)"
                                    ></span>

                                    <template v-if="globalShowViewMoreButton && showViewMore(column, record)">
                                        <div class="position-absolute view-more-parent">
                                            <button
                                                v-if="showViewMoreButton && record['showViewMoreButton_' + column.id]"
                                                class="btn btn-view-more"
                                                @click.stop="showPopupMessage(record, column)"
                                            >
                                                {{ generalTranslations.view_more ?? "View More" }}
                                            </button>
                                            <div
                                                v-if="record['showTooltips_' + column.id]"
                                                class="error-tooltip"
                                            >
                                                {{ record[column.id] }}
                                                <i
                                                    class="icon-close tooltip-close"
                                                    @click="closeTooltip(record, column, index)"
                                                ></i>
                                                <div class="tooltip-arrow"></div>
                                            </div>
                                        </div>
                                    </template>
                                </div>
                            </template>

                            <template v-if="true == column.show_pop_up">
                                <bo-cell-button-popup v-if="record['show_popup_button']">
                                    <template #button="slotProps">
                                        <button
                                            class="btn btn-rounded"
                                            @click="triggerPopup(record, column)"
                                        >
                                            <i class="icon-info"></i>
                                        </button>
                                    </template>
                                </bo-cell-button-popup>

                                <bo-popup-modal
                                    modal-style="full-width has-table"
                                    :active="record['show_popup_modal']"
                                    :title="column.title"
                                    @activate="record['show_popup_modal'] = true"
                                    @deactivate="record['show_popup_modal'] = false"
                                >
                                    <template #body-content>
                                        <bo-datatable-new
                                            id="serial-numbers"
                                            :delete-row="false"
                                            :display-title-header="false"
                                            :columns="popupModalColumns"
                                            :title="column.title"
                                            :records="popupModalRecords"
                                            :pagination="popupModalPagination"
                                            @get_data="
                                                fetchPopupModalRecords(
                                                    column.get_records_route,
                                                    ...arguments,
                                                    record,
                                                    column.payload_parameters
                                                )
                                            "
                                            @generic="
                                                boGenericHandler(
                                                    {
                                                        columns: popupModalColumns,
                                                        records: popupModalRecords,
                                                    },
                                                    ...arguments
                                                )
                                            "
                                        >
                                        </bo-datatable-new>
                                    </template>
                                </bo-popup-modal>
                            </template>
                        </template>
                    </template>

                    <render-slot
                        v-if="hasCtxSlot(`${column.id}_column_right_content`)"
                        :fn="ctxSlot(`${column.id}_column_right_content`)"
                        :params="{ record: record }"
                    ></render-slot>
                    <!-- <div
                                            class="bo-context-menu corner-menu"
                                            v-if="getErrorMessage(record, column.id)"
                                        >
                                            <div class="context-item">
                                                {{ getErrorMessage(record, column.id) }}
                                            </div>
                                        </div> -->
                </div>
            </td>
        </template>

        <td
            class="row-action-cell"
            v-if="displayActionColumn && !isMergedRow(record)"
        >
            <div
                v-if="record['show_action_menu']"
                class="more"
            >
                <i
                    class="icon-morehorizontal cursor-pointer dropdown-trigger"
                    :id="formattedDatatableUniqueKey + '_action_body_button_' + record[primaryKey]"
                    data-toggle="dropdown"
                    aria-haspopup="true"
                    aria-expanded="false"
                ></i>
                <div
                    :id="formattedDatatableUniqueKey + '_actions_column_div_' + record[primaryKey]"
                    class="bo-context-menu-more dropdown-menu"
                    :aria-labelledby="'more-menu-' + record[primaryKey]"
                    @click.stop
                >
                    <template v-if="editSingle && editMultiple">
                        <div
                            id="edit_button"
                            class="context-item"
                            @click="editSingleOrMultiple(record[primaryKey])"
                        >
                            <div class="item-command">
                                <i class="icon-arclight icon-edit"></i>
                                <h6>
                                    {{ generalTranslations.edit ?? "Edit" }}
                                </h6>
                            </div>
                        </div>
                    </template>
                    <template v-if="viewSingle">
                        <div
                            id="view_button"
                            class="context-item"
                            @click="goToUrl(record[primaryKey])"
                        >
                            <div class="item-command">
                                <i class="icon-arclight icon-visibility"></i>
                                <h6>
                                    {{ generalTranslations.view ?? "View" }}
                                </h6>
                            </div>
                        </div>
                    </template>
                    <template v-if="viewLogSingle">
                        <div
                            id="view_records_button"
                            class="context-item"
                            @click="goToLogUrl(record[primaryKey])"
                        >
                            <div class="item-command">
                                <i class="icon-arclight icon-visibility"></i>
                                <h6>
                                    {{ generalTranslations.view_records ?? "View Records" }}
                                </h6>
                            </div>
                        </div>
                    </template>
                    <template v-if="enableEditRecordModal">
                        <div
                            id="edit_record_modal_button"
                            class="context-item"
                            @click="editRecordInModal(record[primaryKey])"
                        >
                            <div class="item-command">
                                <i class="icon-arclight icon-edit"></i>
                                <h6>
                                    {{ generalTranslations.edit ?? "Edit" }}
                                </h6>
                            </div>
                        </div>
                    </template>

                    <render-slot
                        v-if="hasCtxSlot('action_menu')"
                        :fn="ctxSlot('action_menu')"
                        :params="{ record: record, record_id: record[primaryKey] }"
                    ></render-slot>

                    <template v-if="(deleteSingle || deleteMultiple) && record['bo_can_delete']">
                        <div
                            id="delete_button"
                            class="context-item delete-item"
                            @click="deleteSingleOrMultiple(record[primaryKey])"
                        >
                            <div class="item-command">
                                <i
                                    class="icon-arclight icon-deleteforever"
                                    :key="record[primaryKey]"
                                ></i>
                                <h6>{{ generalTranslations.delete ?? "Delete" }}</h6>
                            </div>
                        </div>
                    </template>
                </div>
            </div>
        </td>

        <td
            v-if="!isMergedRow(record)"
            style="min-width: 1px"
        >
            <div>&nbsp;</div>
            <!-- spacer -->
        </td>
    </tr>
</template>
<script>
/**
 * bo-datatable-row
 *
 * A single <tr> extracted from bo-datatable-new so that toggling one row's
 * selection only re-renders THAT row instead of all rows on the page.
 *
 * Design: the row injects the parent datatable instance (`datatableCtx`) and,
 * at runtime, auto-delegates every method (bound) and every data/computed key
 * (as a live reactive getter) that it does not define itself. This keeps the
 * public prop surface tiny (`record`, `index`, `selected`) so Vue can skip
 * re-rendering rows whose props are unchanged. Page-level scoped slots are read
 * straight from the datatable context (never passed as this row's own slots),
 * which is what lets unchanged rows stay un-rendered.
 */
export default {
    name: "bo-datatable-row",

    inject: ["datatableCtx"],

    components: {
        // Renders a page-level scoped slot fn (from the datatable context) inline.
        "render-slot": {
            functional: true,
            render(h, context) {
                const fn = context.props.fn;
                return fn ? fn(context.props.params || {}) : undefined;
            },
        },
    },

    props: {
        record: { type: Object, required: true },
        index: { type: Number, default: 0 },
        selected: { type: Boolean, default: false },
    },

    methods: {
        hasCtxSlot(name) {
            return !!(this.datatableCtx && this.datatableCtx.$scopedSlots[name]);
        },
        ctxSlot(name) {
            return this.datatableCtx ? this.datatableCtx.$scopedSlots[name] : null;
        },
    },

    created() {
        const ctx = this.datatableCtx;
        if (!ctx) {
            return;
        }

        // Delegate every datatable method (bound to the datatable instance) that
        // this row does not already define.
        const methodNames = Object.keys((ctx.$options && ctx.$options.methods) || {});
        methodNames.forEach((name) => {
            if (typeof this[name] === "undefined") {
                this[name] = (...args) => ctx[name](...args);
            }
        });

        // Delegate every data / computed / prop key as a live reactive getter so
        // the row re-renders when the underlying datatable state changes.
        const dataKeys = ctx.$data ? Object.keys(ctx.$data) : [];
        const computedKeys = Object.keys((ctx.$options && ctx.$options.computed) || {});
        const propKeys = ctx.$options && ctx.$options.props ? Object.keys(ctx.$options.props) : [];
        const seen = {};
        dataKeys.concat(computedKeys, propKeys).forEach((name) => {
            if (seen[name]) {
                return;
            }
            seen[name] = true;
            if (name in this) {
                return; // never shadow the row's own props / methods
            }
            Object.defineProperty(this, name, {
                get: () => ctx[name],
                configurable: true,
                enumerable: true,
            });
        });
    },
};
</script>
